import { useInView } from '@/hooks/reveal-in-view';
import { FC, useRef } from 'react';

import type {
  ContentfulCardWithImageType,
  ContentfulFileType,
  ParsedContentNode,
} from '@/types';

import { ContentManager } from '../content-manager';
import { Image } from '../image';
import SplitTextAnimation from '../split-text-animation';

interface Props {
  cards: ContentfulCardWithImageType[];
  footer?: string;
  subtitle: string;
  title: string;
}

interface CardProps {
  delay?: number;
  icon?: ContentfulFileType;
  title?: ParsedContentNode[];
}

const Card: FC<CardProps> = ({ delay, icon, title }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });

  return (
    <div
      className="unified-solution--card"
      onMouseEnter={({ currentTarget }) => {
        currentTarget.classList.add('hover-animation-bounce');
      }}
      onAnimationEnd={({ currentTarget }) => {
        currentTarget.classList.remove('hover-animation-bounce');
      }}
      style={{
        transform: isInView ? 'none' : `translateY(20px)`,
        opacity: isInView ? 1 : 0,
        transition: `all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1) ${delay}s`,
      }}
      ref={ref}
    >
      {icon && (
        <div className="unified-solution--card--icon-wrapper">
          <Image
            src={icon as any}
            alt={icon.title}
            className="unified-solution--card--icon"
          />
        </div>
      )}
      {title && (
        <ContentManager
          content={title}
          className="unified-solution--card--title"
        />
      )}
    </div>
  );
};

export const UnifiedSolution: FC<Props> = ({
  cards,
  footer,
  subtitle,
  title,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });

  return (
    <section className="unified-solution" ref={ref}>
      <div className="unified-solution--wrapper">
        <SplitTextAnimation
          htmlElement={<h2 style={{ opacity: isInView ? 1 : 0 }}>{title}</h2>}
          isInView={isInView}
          className="unified-solution--title"
        />

        <div className="unified-solution--subtitle-wrapper">
          <div className="unified-solution--subtitle-circle" />
          <div className="unified-solution--subtitle-line" />
          <p
            style={{
              transform: isInView ? 'none' : `translateY(20px)`,
              opacity: isInView ? 1 : 0,
              transition: 'all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)',
            }}
            className="unified-solution--subtitle"
          >
            {subtitle}
          </p>
        </div>

        <div className="unified-solution--cards-wrapper">
          {cards.map((card, index) => (
            <Card
              key={index}
              icon={card.image}
              title={card.title}
              delay={index * 0.2}
            />
          ))}
        </div>

        {footer && (
          <div className="unified-solution--subtitle-wrapper">
            <div className="unified-solution--subtitle-circle" />
            <div className="unified-solution--subtitle-line" />
            <p
              style={{
                transform: isInView ? 'none' : `translateY(20px)`,
                opacity: isInView ? 1 : 0,
                transition: 'all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)',
              }}
              className="unified-solution--subtitle"
            >
              {footer}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};
